import { useEffect, useRef, useState } from 'react'
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import './styles.css'
import { useEnv } from './context'
import { useHubQueueRunner } from './data/hubQueue/runner'
import { screenOf, type LegacyWhere } from './legacyUsage'
import { resolveScheme, useThemeChoice } from './platform/theme'
import { AiActionScreen } from './screens/AiActionScreen'
import { AthleteEditScreen, AthleteNewScreen, AthleteSportScreen } from './screens/AthleteFormScreens'
import { AthleteScreen } from './screens/AthleteScreen'
import { AthletesScreen } from './screens/AthletesScreen'
import { ChannelsScreen, ClubScreen, ClubSwitchScreen, DirectionsScreen, TeamScreen } from './screens/ClubScreens'
import {
  AboutScreen,
  ClubDeleteScreen,
  ClubJoinScreen,
  ClubRenameScreen,
} from './screens/ClubSettingsScreens'
import { FeedbackListScreen, FeedbackNewScreen, FeedbackThreadScreen } from './screens/FeedbackScreens'
import { CoachEditScreen, CoachNewScreen, CoachScreen, CoachesScreen } from './screens/CoachScreens'
import { DirectionEditScreen, DirectionNewScreen, DirectionScreen } from './screens/DirectionScreens'
import { EventAddScreen } from './screens/EventAddScreen'
import { EventFormScreen, EventScreen, EventsScreen } from './screens/EventScreens'
import { GroupScheduleEditScreen } from './screens/GroupScheduleEditScreen'
import { GroupScreen, GroupsScreen, PaymentsPickScreen } from './screens/GroupScreens'
import { GroupEditScreen, GroupNewScreen } from './screens/GroupForms'
import { HubBotScreen } from './screens/HubBotScreen'
import { HubChatScreen } from './screens/HubChatScreen'
import { MaterialFormScreen, MaterialScreen, MaterialsScreen } from './screens/MaterialScreens'
import { AthleteNoteEditScreen, GroupNoteEditScreen } from './screens/NotesScreens'
import { AthleteNotesTrashScreen, GroupNotesTrashScreen, MaterialsTrashScreen } from './screens/TrashScreens'
import { SetupScreen } from './screens/SetupScreen'
import { PaymentStatusScreen, PlansScreen, SubscriptionScreen, TierChangeScreen } from './screens/SubscriptionScreens'
import { ScheduleScreen } from './screens/ScheduleScreen'
import { SessionScreen } from './screens/SessionScreen'
import { SessionNewcomerScreen } from './screens/SessionNewcomerScreen'
import { SessionRollCallScreen } from './screens/SessionRollCallScreen'
import { TodayScreen } from './screens/TodayScreen'

/**
 * Срез редизайна v1: «Сегодня → Расписание → Занятие → Посещаемость».
 * Маршруты логические (ТЗ стр. 23): today, schedule, sessions/:key. Роутер
 * даёт среда-хозяин (BrowserRouter в PWA, HashRouter на стенде), поэтому
 * системная кнопка «назад» браузера работает как в обычном приложении.
 */
export function RedesignApp({
  theme,
  onLegacy,
  onSignOut,
}: {
  theme?: 'light' | 'dark'
  /** Вернуться в прежний интерфейс: на боевом подключении. where - для счётчика открытий. */
  onLegacy?: (where: LegacyWhere) => void
  /** Выход из аккаунта: только PWA (в мини-приложениях вход через мессенджер). */
  onSignOut?: () => void | Promise<void>
}) {
  const { platform } = useEnv()
  // Тема: заданная стендом или выбранная на устройстве (этап 5 шаг 4)
  const choice = useThemeChoice()
  const [scheme, setScheme] = useState(theme ?? resolveScheme(choice, platform))
  // Очередь Штаба уходит при открытии приложения и появлении сети, на любом экране
  useHubQueueRunner()
  useEffect(() => setScheme(theme ?? resolveScheme(choice, platform)), [theme, platform, choice])
  const trail = useRouteTrail()
  const legacy = onLegacy ? () => onLegacy({ screen: screenOf(trail.current.now), fromScreen: screenOf(trail.current.prev) }) : undefined

  return (
    <div className="pc2" data-theme={scheme}>
      <Routes>
        <Route path="/" element={<Navigate to="/today" replace />} />
        <Route path="/today" element={<TodayScreen />} />
        <Route path="/setup" element={<SetupRoute />} />
        <Route path="/schedule" element={<ScheduleScreen />} />
        <Route path="/sessions/:key" element={<SessionScreen />} />
        {/* Занятие: перекличка текстом и голосом, новичок (этап 5, шаг 2). */}
        <Route path="/sessions/:key/roll-call" element={<SessionRollCallScreen />} />
        <Route path="/sessions/:key/newcomer" element={<SessionNewcomerScreen />} />
        {/* Соревнования, сборы, аттестации - внутри расписания (events.md). */}
        <Route path="/schedule/events" element={<EventsScreen />} />
        <Route path="/schedule/events/new" element={<EventFormScreen />} />
        <Route path="/schedule/events/:id" element={<EventScreen />} />
        <Route path="/schedule/events/:id/edit" element={<EventFormScreen />} />
        <Route path="/schedule/events/:id/add" element={<EventAddScreen />} />
        <Route path="/athletes" element={<AthletesScreen />} />
        <Route path="/athletes/new" element={<AthleteNewScreen />} />
        <Route path="/athletes/:id" element={<AthleteScreen />} />
        <Route path="/athletes/:id/edit" element={<AthleteEditScreen />} />
        <Route path="/athletes/:id/sport" element={<AthleteSportScreen />} />
        {/* Записи и материалы (этап 5 шаг 3): правка и удаление записи, материалы направления. */}
        <Route path="/athletes/:id/notes/trash" element={<AthleteNotesTrashScreen />} />
        <Route path="/athletes/:id/notes/:noteId" element={<AthleteNoteEditScreen />} />
        <Route path="/groups/:id/notes/trash" element={<GroupNotesTrashScreen />} />
        <Route path="/groups/:id/notes/:noteId" element={<GroupNoteEditScreen />} />
        <Route path="/club/materials" element={<MaterialsScreen />} />
        <Route path="/club/materials/new" element={<MaterialFormScreen />} />
        <Route path="/club/materials/trash" element={<MaterialsTrashScreen />} />
        <Route path="/club/materials/:id" element={<MaterialScreen />} />
        <Route path="/club/materials/:id/edit" element={<MaterialFormScreen />} />
        <Route path="/groups" element={<GroupsScreen />} />
        <Route path="/groups/:id" element={<GroupScreen />} />
        {/* Группы целиком (этап 5 шаг 1): создание вне мастера и правка. */}
        <Route path="/groups/new" element={<GroupNewScreen />} />
        <Route path="/groups/:id/edit" element={<GroupEditScreen />} />
        <Route path="/groups/:id/schedule" element={<GroupScheduleEditScreen />} />
        <Route path="/club" element={<ClubScreen onLegacy={legacy} onSignOut={onSignOut} />} />
        <Route path="/club/directions" element={<DirectionsScreen />} />
        {/* Направления, справочник тренеров, мелочи клуба (этап 5 шаг 4). */}
        <Route path="/club/directions/new" element={<DirectionNewScreen />} />
        <Route path="/club/directions/:id" element={<DirectionScreen />} />
        <Route path="/club/directions/:id/edit" element={<DirectionEditScreen />} />
        <Route path="/club/coaches" element={<CoachesScreen />} />
        <Route path="/club/coaches/new" element={<CoachNewScreen />} />
        <Route path="/club/coaches/:id" element={<CoachScreen />} />
        <Route path="/club/coaches/:id/edit" element={<CoachEditScreen />} />
        <Route path="/club/rename" element={<ClubRenameScreen />} />
        <Route path="/club/join" element={<ClubJoinScreen />} />
        <Route path="/club/delete" element={<ClubDeleteScreen />} />
        {/* Обратная связь как переписка: список, новое обращение, тред. */}
        <Route path="/club/feedback" element={<FeedbackListScreen />} />
        <Route path="/club/feedback/new" element={<FeedbackNewScreen />} />
        <Route path="/club/feedback/:id" element={<FeedbackThreadScreen />} />
        <Route path="/club/about" element={<AboutScreen />} />
        <Route path="/club/switch" element={<ClubSwitchScreen />} />
        <Route path="/club/channels" element={<ChannelsScreen />} />
        <Route path="/club/team" element={<TeamScreen />} />
        <Route path="/club/subscription" element={<SubscriptionScreen />} />
        <Route path="/club/subscription/plans" element={<PlansScreen />} />
        <Route path="/club/subscription/change/:tier" element={<TierChangeScreen />} />
        {/* Возврат со страницы оплаты: /club/subscription/payment/return (PWA) или id платежа. */}
        <Route path="/club/subscription/payment/:id" element={<PaymentStatusScreen />} />
        <Route path="/payments" element={<PaymentsPickScreen />} />
        {/* Штаб в PWA (S13). В мини-приложениях кнопка Штаба ведёт в бота, сюда не попадают. */}
        <Route path="/hub" element={<HubChatScreen />} />
        {/* S14: проверка массового действия Штаба - точный список и подтверждение. */}
        <Route path="/hub/actions/:id" element={<AiActionScreen />} />
        {/* S16: из мини-приложения в бота с контекстом экрана. */}
        <Route path="/hub/bot" element={<HubBotScreen />} />
        <Route path="*" element={<Navigate to="/today" replace />} />
      </Routes>
    </div>
  )
}

/** Текущий и предыдущий экран: откуда ушли в прежний интерфейс (этап 5 шаг 1). */
function useRouteTrail() {
  const { pathname } = useLocation()
  const trail = useRef<{ now?: string; prev?: string }>({})
  useEffect(() => {
    if (trail.current.now !== pathname) trail.current = { now: pathname, prev: trail.current.now }
  }, [pathname])
  return trail
}

/** Мастер как маршрут: на стенде и для повторной настройки. Боевой вход - LiveRedesign. */
function SetupRoute() {
  const navigate = useNavigate()
  return <SetupScreen onDone={() => navigate('/today', { replace: true })} />
}
